const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/store/zustaendigkeit.ts';
import Imm from 'immutable';
import { createSelector } from 'reselect';
import { Logger } from '@cistec/common_web/services/KisLoggerService';
import { decCounter, incCounter } from '@cistec/common_web/utils/reducerUtils';
import { KisApiAction, ReducerState } from '@cistec/common_web/defs/actionTypes';
import { IconTextTuple } from 'App/pages/DashboardPage/IconTextTuples';
import { useRemoteData } from '@cistec/common_web/hooks/useRemoteData';
import { selectCurrentCaseNr } from '@cistec/common_web/selectors/casesSelectors';
import { loadPatResponsibilities, REQUEST_ACTION_TYPES_LOAD_PAT_RESPONSIBILITIES } from 'App/actions/zustaendigkeit';
import { PatAppStoreState } from './setup/rootReducer';
import { getPatNrFromSession } from 'App/utils/sessionStorage';

const emptyMap = Imm.Map<any, any>(); // used to avoid creating new instances every time

const Log = Logger('responsibilityReducer');

const initialState = Imm.fromJS({
    loading: {
        // Maps patnr -> count
    },

    records: {
        // Maps workId -> record
    },

    index: {
        byPat: {
            // Maps patnr -> workId -> true
        },
    },
});

export function responsibilityReducer(state: ReducerState = initialState, action: any = {}) {
    switch (action.type) {
        case REQUEST_ACTION_TYPES_LOAD_PAT_RESPONSIBILITIES.pending:
            return loadPatResponsibilitiesPending(state, action);
        case REQUEST_ACTION_TYPES_LOAD_PAT_RESPONSIBILITIES.success:
            return loadPatResponsibilitiesSuccess(state, action);
        case REQUEST_ACTION_TYPES_LOAD_PAT_RESPONSIBILITIES.failure:
            return loadPatResponsibilitiesFailure(state, action);
    }

    return state;
}

function loadPatResponsibilitiesPending(state: ReducerState, action: KisApiAction) {
    return incCounter(state, ['loading', action.meta.patnr]);
}

function loadPatResponsibilitiesSuccess(state: ReducerState, action: KisApiAction) {
    const list = Imm.fromJS(action.payload.List);

    list.forEach((rec: Imm.Map<string, any>, _idx: any) => {
        const workId = rec.get('WORKID');

        if (!workId) {
            Log.warn('Ignoring record with missing WORKID');
        } else {
            state = state.setIn(['records', workId], rec);

            const patnr = rec.get('PATNR');

            if (patnr) {
                state = state.setIn(['index', 'byPat', patnr, workId], true);
            }
        }
    });

    return decCounter(state, ['loading', action.meta.patnr]);
}

function loadPatResponsibilitiesFailure(state: ReducerState, action: KisApiAction) {
    Log.warn(`Failed to load responsibility for pat: ${action.meta.patnr}`);
    return decCounter(state, ['loading', action.meta.patnr]);
}

export const responsibilityLoadingRoot = (state: PatAppStoreState) => state.responsibility.get('loading') || emptyMap;

export const responsibilityRecordsRoot = (state: PatAppStoreState) => state.responsibility.get('records');

export const responsibilityIndexByPat = (state: PatAppStoreState) =>
    state.responsibility.getIn(['index', 'byPat']) || emptyMap;

const responsibilityIndexOfCurPat = createSelector(
    [responsibilityIndexByPat],
    (responsibilityIndexByPat: Imm.Map<string, any>): Imm.Map<string, boolean> => {
        const patNr = getPatNrFromSession();
        if (!responsibilityIndexByPat || !patNr) {
            return emptyMap;
        }

        return responsibilityIndexByPat.get(patNr) || emptyMap;
    }
);

export const responsibilitiesOfCurPat = createSelector(
    [responsibilityRecordsRoot, responsibilityIndexOfCurPat],
    (
        responsibilityRecordsRoot: Imm.Map<string, any>,
        responsibilityIndexOfCurPat: Imm.Map<string, boolean>
    ): Imm.Map<string, any> => {
        if (!responsibilityRecordsRoot || !responsibilityIndexOfCurPat) {
            return emptyMap;
        }

        return responsibilityIndexOfCurPat.reduce((result: Imm.Map<string, any>, _val: boolean, workId: string) => {
            return result.set(workId, responsibilityRecordsRoot.get(workId));
        }, emptyMap);
    }
);

export const isLoadingResponsibilitiesOfCurPat = createSelector(
    [responsibilityLoadingRoot],
    (loadingRoot: Imm.Map<string, any>): boolean => {
        const patNr = getPatNrFromSession();
        return !!(loadingRoot && patNr && (loadingRoot.get('patnr') || 0) > 0);
    }
);

// FIXME: MOVE (use of) THESE UTILS OUT OF THIS FILE
// in short, selectors should probably not do anything with icons or other display related stuff

/**
 * Maps TKEY to icon name
 * FIXME move this to the ResponsibilitiesCard folder (or maybe utils)
 * @see MetaKat cbd120e8-96ec-4dc7-90c7-73098b629a99 (COM)
 * @see MetaKat c744e371-a95d-41a1-8ebb-f895b386ef92 (SZB)
 * @see MetaKat fc2e778d-dc83-4845-b96d-50f1e336dc1c (KSA)
 */
const mapTkeyToIconName: any = {
    ARZT: 'icons8-physician',
    AZAZ: 'icons8-physician',
    AZTAZT: 'icons8-physician',
    AZTASS: 'icons8-physician',
    AZTKAD: 'icons8-physician',
    AZTBEL: 'icons8-physician',
    AZTOPE: 'icons8-physician',
    AZASS: 'icons8-physician',
    AZOA: 'icons8-physician',
    AZLEI: 'icons8-physician',
    AZABT: 'icons8-physician',
    AZDIR: 'icons8-physician',
    AZFACH: 'icons8-physician',
    PFLEGE: 'icons8-nurse',
    PFLFACH: 'icons8-nurse',
    PFLASS: 'icons8-nurse',
    PFLBEZ: 'icons8-nurse',
    PFLABT: 'icons8-nurse',
    PFLPFK: 'icons8-nurse',
    'ERB-TM': 'icons8-nutrition',
    'DIB-TM': 'icons8-finger-blood',
    'SOZ-TM': 'icons8-handshake',
    'FAPRI-MEDDER': 'icons8-antiseptic-cream',
    'FAPRI-MEDINF': 'icons8-bacteria',
    'FAPRI-MEDKAR': 'icons8-medical-heart',
    'FAPRI-MEDNEU': 'icons8-brain',
    'FASEK-MEDDER': 'icons8-antiseptic-cream',
    'FASEK-MEDEDM': 'icons8-nutrition',
    'FASEK-MEDINF': 'icons8-bacteria',
    'FASEK-MEDKAR': 'icons8-medical-heart',
    'FASEK-MEDNEU': 'icons8-brain',
    'FASEK-CHIPLA': 'icons8-whole-hand',
    'FASEK-KKJAPIN': 'icons8-bacteria',
    'FASEK-KKJAPKA': 'icons8-medical-heart',
    'FASEK-KKJANPA': 'icons8-brain',
    'FASEK-KKJANPS': 'icons8-brain',
};

function getResponsibilityIcon(rec: Imm.Map<string, any>) {
    const tkat = (rec && rec.get('TKEY')) || '?';
    return mapTkeyToIconName[tkat] || 'icons8-user-group';
}

function buildPrimaryTextForResponsibilityItem(rec: Imm.Map<string, any>) {
    const recipients = rec.getIn(['CONTENT', 'Recipients']);
    const firstRecipient = (recipients || emptyMap).first();

    if (firstRecipient) {
        const kat = firstRecipient.get('Kat');
        const displayName = firstRecipient.get('DisplayName').replace(` <${firstRecipient.get('Key')}>`, '');

        if (kat && kat !== 'USER') {
            // This is probably a Zuständigkeit sent to a POOL instead of a USER.
            // Is this useful information to a PatApp user? I doubt it.
            // To remove this item altogether, change the selector on DashboardPage.
            // If this should be kept in, the following should be textMapped instead.
            // Either way, FIXME
            return 'Abteilung';
        } else if (displayName) {
            return displayName;
        }
    }

    return rec.get('RKEY') || '?';
}

export const listOfResponsibilities = createSelector(
    [responsibilitiesOfCurPat],
    (responsibilities: Imm.Map<string, any>): IconTextTuple[] => {
        if (!Imm.Map.isMap(responsibilities) || responsibilities.isEmpty()) {
            return [];
        }

        /**
         * The first four characters of the subject are used to determine the sorting
         */
        const sortingOrder = ['arzt', 'ärzt', 'pfle'];

        return responsibilities
            .sortBy((rec: Imm.Map<string, any>) => {
                const subject = rec.get('SUBJECT') || '?';
                const idx = sortingOrder.indexOf(subject.slice(0, 4));
                const paddedIdx = idx < 0 ? '99' : `00${idx}`.slice(-2);
                return `${paddedIdx}${subject}`;
            })
            .map((rec: Imm.Map<string, any>) => {
                // build list of responsibilities for dashboard card
                return {
                    primaryText: buildPrimaryTextForResponsibilityItem(rec),
                    secondaryText: rec.get('SUBJECT') || '?',
                    icon: getResponsibilityIcon(rec),
                };
            })
            .toArray();
    }
);

// HOOKS

export function useResponsibilityData() {
    return useRemoteData({}, () => ({
        isLoading: isLoadingResponsibilitiesOfCurPat,
        select: listOfResponsibilities,
        shouldFetch: (state, _props, { isLoading }) => {
            const patnr = getPatNrFromSession();
            const fallnr = selectCurrentCaseNr(state);
            const resp = responsibilitiesOfCurPat(state);
            return !!patnr && !!fallnr && (!resp || !resp.size) && !isLoading;
        },
        fetch: (dispatch, state) => {
            const patnr = getPatNrFromSession();
            const fallnr = selectCurrentCaseNr(state);
            dispatch(loadPatResponsibilities(patnr, fallnr, undefined, undefined, true));
        },
        noRefetch: true,
    }));
}
